import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { PhotoIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Redeem = () => {
  const { restaurantId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billImage, setBillImage] = useState(null);
  const [manualAmount, setManualAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transaction, setTransaction] = useState(null);
  const [ocrText, setOcrText] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBillImage(file);
      setError('');
    }
  };

  const handleUploadBill = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!billImage && !manualAmount) {
      setError('Please upload a bill image or enter amount manually');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      if (billImage) {
        formData.append('billImage', billImage);
      }
      formData.append('restaurantId', restaurantId);
      if (manualAmount) {
        formData.append('manualAmount', manualAmount);
      }

      const response = await axios.post(`${API_URL}/redeem/upload-bill`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.requiresManualInput) {
        setError('Could not extract bill amount. Please enter it manually.');
        setOcrText(response.data.ocrText || '');
        setLoading(false);
        return;
      }

      setTransaction(response.data.transaction);
      
      // If amount to pay > 0, proceed with payment
      if (response.data.transaction.amountToPay > 0) {
        await processPayment(response.data.transaction);
      } else {
        // Payment complete, all covered by points
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to process bill');
      setLoading(false);
    }
  };

  const processPayment = async (txn) => {
    try {
      await loadRazorpayScript();

      // Create payment order
      const orderResponse = await axios.post(`${API_URL}/payments/restaurant/create-order`, {
        restaurantId,
        billAmount: txn.billAmount,
        discountApplied: txn.discountApplied
      });

      const options = {
        key: orderResponse.data.key,
        amount: orderResponse.data.amount,
        currency: 'INR',
        name: 'LOCU',
        description: `Bill payment at restaurant`,
        order_id: orderResponse.data.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API_URL}/payments/restaurant/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: txn.id
            });

            navigate('/dashboard');
          } catch (error) {
            setError('Payment verification failed');
          } finally {
            setLoading(false);
          }
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create payment order');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Redeem Points</h1>

        {transaction ? (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <CurrencyDollarIcon className="h-16 w-16 text-primary-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bill Processed!</h2>
              <p className="text-gray-600">Complete payment to finish redemption</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Bill Amount</span>
                <span className="font-bold">₹{transaction.billAmount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between p-4 bg-primary-50 rounded-lg">
                <span className="font-medium text-primary-800">Discount Applied (10%)</span>
                <span className="font-bold text-primary-600">-₹{transaction.discountApplied.toLocaleString()}</span>
              </div>
              <div className="flex justify-between p-4 bg-primary-50 rounded-lg border-2 border-primary-200">
                <span className="font-medium text-primary-900">Amount to Pay</span>
                <span className="font-bold text-primary-600 text-xl">₹{transaction.amountToPay.toLocaleString()}</span>
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center">
              Opening UPI payment...
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleUploadBill} className="space-y-6">
              <div>
                <label htmlFor="billImage" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Bill Photo
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition">
                  <div className="space-y-1 text-center">
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="billImage" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                        <span>Upload a file</span>
                        <input
                          id="billImage"
                          name="billImage"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    {billImage && (
                      <p className="text-sm text-primary-600 mt-2">✓ {billImage.name}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center text-gray-500">OR</div>

              <div>
                <label htmlFor="manualAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Bill Amount Manually (₹)
                </label>
                <input
                  type="number"
                  id="manualAmount"
                  name="manualAmount"
                  step="0.01"
                  min="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                />
              </div>

              {ocrText && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">OCR Extracted Text:</p>
                  <p className="text-sm text-gray-800">{ocrText.substring(0, 200)}...</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (!billImage && !manualAmount)}
                className="w-full bg-primary-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Process Bill & Redeem Points'}
              </button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> Upload your bill photo or enter amount manually. 
                We'll apply a 10% discount (up to your available points) and you pay the remaining amount via UPI.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Redeem;

